import type { RouteLeadPayload } from '@/services/api/callcenter';
import { CallcenterWizardData } from '@/types/callcenter';
import { Buchungsmodus } from '@/store/callcenter/buchungsmodus';
import { hatPartner } from '@/utils/haushalt';

/**
 * Single Source für den Vorschau-Payload (`POST /callcenter/route-lead`).
 *
 * Gegenstück zu {@link buildWizardCustomerPayload}: derselbe Wizard-Stand, zwei
 * Endpunkte, und **eine** Regel dafür, ob der Partner mitzählt (`hatPartner`,
 * `utils/haushalt`).
 *
 * WARUM DAS EINE EIGENE FUNKTION IST UND NICHT INLINE IM STORE STEHT. Genau
 * hier lag die Rest-Divergenz nach Decision #146: der Payload wurde in der
 * Store-Aktion `routeLead` zusammengebaut und schickte `partner_net_salary`,
 * `partner_additional_income` und `partner_birth_year` **bedingungslos** mit —
 * während `buildWizardCustomerPayload` `partner_data` bei einem
 * Nicht-Partner-Haushalt auf `null` setzt. Dieselbe Person ergab damit in
 * Vorschau und Buchung verschiedene Zahlen. Als Store-Interna war das nicht
 * testbar; als Funktion lassen sich beide Bauer aus EINEM Wizard-Stand
 * vergleichen (`__tests__/payloadSymmetrie.test.ts`).
 *
 * `null` statt Weglassen: `RouteLeadRequest` validiert alle Lead-Felder als
 * `nullable`, und `PoolDistributionService::leadKennzahlen` prüft mit `isset()`
 * — `null` verhält sich dort exakt wie ein fehlender Schlüssel und damit
 * identisch zum `partner_data: null` der Buchung.
 */
export function buildRouteLeadPayload(
  wizardData: CallcenterWizardData,
  modus: Buchungsmodus,
): RouteLeadPayload {
  const mitPartner = hatPartner(wizardData.haushaltssituation);

  return {
    // DER MODUS GEHOERT IN DIE VORSCHAU (07.08.2026). Hier stand fest `'pool'`,
    // weil der Modus damals nur die ZUWEISUNG betraf und den Pool nicht. Seit
    // die Bindung eine Wahl ist, bestimmt er mit, welcher Pool herauskommt —
    // und die Vorschau speist die Slot-Anzeige. Zeigte sie einen anderen Pool
    // als den gebuchten, saehe der Agent die Slots einer Verteilung, die fuer
    // seine Buchung nie galt (Decision #146).
    //
    // PFLICHTPARAMETER und kein Default: ein vergessener Modus wuerde
    // gebundenen Agenten stillschweigend die Bindung nehmen. Der Compiler soll
    // die Stelle finden, nicht der Betrieb.
    mode: modus,
    // Leadherkunft (Step-5-Pflicht, BE nullable) — steuert das KreditLeads-
    // Routing: kein Mini + lead_source='Kreditanfrage' → kreditleads-eb.
    lead_source: wizardData.leadherkunft,

    // Mandant — zählt immer.
    net_salary: wizardData.nettogehalt1,
    additional_income: wizardData.einkuenfteHoehe,
    birth_year: wizardData.geburtsjahr,

    // Partner — nur bei Paar/verheiratet, exakt wie `partner_data` in der
    // Buchung. Die Werte bleiben im Store stehen (bewusst, s. utils/haushalt),
    // sie dürfen nur nicht in die Einstufung einfließen.
    partner_net_salary: mitPartner ? wizardData.nettogehalt2 : null,
    partner_additional_income: mitPartner ? wizardData.einkuenfteHoehe_partner : null,
    partner_birth_year: mitPartner ? wizardData.geburtsjahrPartner : null,

    // RC-Auffangnetz (Decision #171, 19.08.2026). Die beiden Felder routen
    // NICHTS — sie sind die Kontaktmerkmale, an denen der Server die RC-Weiche
    // aufhaengt (#166 R4: Telefon-Anker + Mail-Netz, nie eine `customer_id`).
    // Erst mit dem aufgeloesten Pool laesst sich beantworten, ob der gepinnte
    // Alt-VP im Fenster ueberhaupt etwas frei hat; deshalb reisen sie mit der
    // POOL-VORSCHAU und nicht mit der Kontaktsuche.
    //
    // Leere Strings bewusst auf `null`: der FormRequest validiert `email` als
    // Mailadresse, und ein leeres Feld aus einem Step-5-uebersprungenen Pfad
    // wuerde die ganze Vorschau mit 422 abraeumen — also Kalender weg, wegen
    // einer Auskunft, die es nur zusaetzlich gibt.
    phone: wizardData.telefonnummer || null,
    email: wizardData.email || null,
  };
}
